import React from 'react';
import { ANALYTICS_ENTRY_POINT_STATE_KEY } from '@/utils/analytics/reportEntryPoint';

import { Formik, useFormikContext } from 'formik';
import { useMakeFilteredUrl } from '@/hooks/useFilter';
import { useHistory } from 'react-router';
import {
  FuelParticipation,
  MachineFieldsFragment,
  useGetMachineByIdQuery,
} from '@/generated/codegen';
import { ReportType } from '@/modules/reports/ReportTypes';
import {
  machineAcceptsReportType,
  machineReportTypeRejection,
} from '@/modules/reports/machineReportRules';
import {
  parseQR,
  QRReaderWithFallback,
} from '@/components/QRReaderWithFallback';
import { cn } from '@/utils/utils';
import { FaArrowRight } from '@react-icons/all-files/fa/FaArrowRight';
import { UserQR } from '@/components/UserQR';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { WatchQueryFetchPolicy } from '@apollo/client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { isEffectivelyOnline } from '@bilderit/offline-link';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ConnectedMachineCombobox } from '@/components/Inputs/ConnectedMachineCombobox';

const FullScreenQRScanner: React.FC<{
  isOpen: boolean;
  toggle: () => void;
  reportType?: ReportType;
}> = ({ isOpen, toggle, reportType }) => {
  const firstSnapPoint = '80px';
  const [snap, setSnap] = React.useState<string | number | null>(
    firstSnapPoint
  );
  const make = useMakeFilteredUrl();
  const history = useHistory();
  const vals = {
    machine_name: '',
    machine: undefined as MachineFieldsFragment | undefined,
    form: { message: '' },
  };

  const makeReportUrl = React.useCallback(
    (machine: MachineFieldsFragment) =>
      make(
        reportType === ReportType.DailyReport
          ? `/my-reports/${ReportType.DailyReport}/new/${machine.id}`
          : reportType === ReportType.MachineNotice
            ? `/my-reports/${ReportType.MachineNotice}/new/${machine.id}`
            : reportType === ReportType.FuelReport
              ? [
                  FuelParticipation.CompanyStorage,
                  FuelParticipation.Storage,
                ].includes(machine.fuel_participation!)
                ? `/my-reports/${ReportType.FuelReport}/new/source/${machine.id}`
                : `/my-reports/${ReportType.FuelReport}/new/${machine.id}`
              : reportType === ReportType.StockReport
                ? `/my-reports/${ReportType.StockReport}/new/${machine.id}`
                : reportType === ReportType.Checklist
                  ? `/my-reports/${ReportType.Checklist}/new/${machine.id}`
                  : `/machine/${machine.id}`
      ),
    [make, reportType]
  );
  const navigateToReport = React.useCallback(
    (machine: MachineFieldsFragment, entry: 'qr_scan' | 'qr_search') =>
      history.replace(makeReportUrl(machine), {
        [ANALYTICS_ENTRY_POINT_STATE_KEY]: entry,
      }),
    [history, makeReportUrl]
  );
  const pendingScanNavigate = React.useRef(false);
  // Memoized so ValidateAccessToMachine's validation effect (which lists
  // these in its dependency array) doesn't re-fire on every unrelated
  // parent render — an inline arrow here would give it a new identity each
  // time, re-running the effect after every render and looping forever
  // once a machine is validated (validated machine_id is never cleared on
  // the success path).
  const handleValidated = React.useCallback(
    (machine: MachineFieldsFragment) => {
      if (!pendingScanNavigate.current) return;
      pendingScanNavigate.current = false;
      navigateToReport(machine, 'qr_scan');
    },
    [navigateToReport]
  );
  const handleValidationFailed = React.useCallback(() => {
    pendingScanNavigate.current = false;
  }, []);

  return (
    <Formik
      initialValues={vals}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        if (values.machine?.id) navigateToReport(values.machine, 'qr_search');
        setSubmitting(false);
      }}
    >
      {({
        values,
        isSubmitting,
        setFieldValue,
        setSubmitting,
        submitForm,
        resetForm,
      }) => (
        <>
          <ValidateAccessToMachine
            inputMachineIdField="raw_machine_id"
            validatedMachineField="machine"
            reportType={reportType}
            onValidated={handleValidated}
            onValidationFailed={handleValidationFailed}
          />
          <Dialog
            open={isOpen}
            onOpenChange={(o) => {
              toggle();
              if (!o) {
                resetForm();
                pendingScanNavigate.current = false;
                setSubmitting(false);
              }
            }}
          >
            <DialogContent className="h-dvh grid grid-rows-[auto_1fr] pointer-events-auto!">
              <div
                className={cn(
                  'absolute flex w-full h-full opacity-30 bg-black z-20 items-center justify-center',
                  { hidden: !isSubmitting }
                )}
              >
                <LoadingSpinner className="size-24 stroke-white fill-white" />
              </div>
              <DialogHeader>
                <DialogTitle>Elegi una maquina</DialogTitle>
              </DialogHeader>
              <div
                className={cn('flex kbc:pb-10 overflow-hidden justify-center')}
              >
                <div
                  className={cn(
                    'relative content-center text-center w-full h-full'
                  )}
                >
                  <QRReaderWithFallback
                    disabled={isSubmitting}
                    delay={300}
                    onScan={(code: string) => {
                      if (!code) return;
                      const { machine_id, error } = parseQR(code);
                      if (error || !machine_id) {
                        toast.error(
                          'Ese código QR no es de un equipo Bilderit. Por favor intentá de nuevo.'
                        );
                        return;
                      }
                      if (values.machine?.id === Number(machine_id)) {
                        navigateToReport(values.machine, 'qr_scan');
                        return;
                      }
                      pendingScanNavigate.current = true;
                      setFieldValue('raw_machine_id', machine_id);
                    }}
                    style={{ objectFit: 'cover' }}
                    showButton={false}
                  />
                  <form onSubmit={submitForm}>
                    <div
                      className={cn(
                        'justify-self-center inline-flex gap-4 absolute w-4/5 max-w-md self-center bottom-8 ml-0 mr-0 left-0 right-0 rounded-md '
                      )}
                    >
                      <ConnectedMachineCombobox
                        name="machine"
                        autoFocus={false}
                        className={cn(
                          'bg-white rounded-md w-full opacity-60 focus-within:opacity-100'
                        )}
                        popoverProps={{ side: 'top', align: 'center' }}
                        filterMachines={(machine) =>
                          machineAcceptsReportType(machine, reportType)
                        }
                      />
                      {values.machine?.id && (
                        <Button
                          type="submit"
                          size="lg-icon"
                          className={cn('inline-block h-9 shrink-0')}
                          disabled={!values.machine.id}
                          onClick={submitForm}
                        >
                          <FaArrowRight />
                        </Button>
                      )}
                      <Drawer
                        open={true}
                        snapPoints={[firstSnapPoint, '550px']}
                        activeSnapPoint={snap}
                        setActiveSnapPoint={setSnap}
                        modal={false}
                        dismissible={false}
                      >
                        <DrawerContent
                          title="Mostrá tu QR"
                          className={cn('kbo:opacity-0 h-dvh')}
                        >
                          <div
                            className={cn(
                              'self-center transition-all p-4',
                              snap !== firstSnapPoint &&
                                'translate-y-[calc((100dvh-var(--snap-point-height))/2-50%)]'
                            )}
                          >
                            <UserQR className={cn('w-[70dvw]')} />
                          </div>
                        </DrawerContent>
                      </Drawer>
                    </div>
                  </form>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </Formik>
  );
};

export default FullScreenQRScanner;

export type ValidateAccessToMachineProps = {
  inputMachineIdField: string;
  validatedMachineField: string;
  reportType?: ReportType;
  fetchPolicy?: WatchQueryFetchPolicy;
  onValidated?: (machine: MachineFieldsFragment) => void;
  onValidationFailed?: () => void;
};

export const ValidateAccessToMachine: React.FC<
  ValidateAccessToMachineProps
> = ({
  inputMachineIdField,
  validatedMachineField,
  reportType,
  fetchPolicy,
  onValidated,
  onValidationFailed,
}) => {
  const { values, setFieldValue, setSubmitting } = useFormikContext<any>();
  const machineId = values[inputMachineIdField];

  const { data, loading } = useGetMachineByIdQuery({
    variables: { id: machineId! },
    skip: !machineId,
    fetchPolicy:
      (fetchPolicy ?? isEffectivelyOnline('3g')) ? 'cache-first' : 'cache-only',
    notifyOnNetworkStatusChange: true,
  });

  const machine = React.useMemo(
    () =>
      data?.getMachineById &&
      'item' in data.getMachineById &&
      data.getMachineById.item,
    [data]
  );

  React.useEffect(() => {
    if (machineId)
      if (loading) setSubmitting(true);
      else {
        if (!machine || !machine?.id) {
          toast.error(
            'No tienes acceso al equipo que estás intentando leer, o no está disponible.'
          );
          setFieldValue(inputMachineIdField, '');
          onValidationFailed?.();
        } else {
          const rejection = machineReportTypeRejection(machine, reportType);
          if (rejection) {
            toast.error(rejection);
            setFieldValue(inputMachineIdField, '');
            onValidationFailed?.();
          } else {
            setFieldValue(validatedMachineField, machine);
            onValidated?.(machine);
          }
        }
        setTimeout(() => setSubmitting(false), 500);
      }
  }, [
    inputMachineIdField,
    loading,
    machine,
    machineId,
    onValidated,
    onValidationFailed,
    reportType,
    setFieldValue,
    setSubmitting,
    validatedMachineField,
  ]);

  return null;
};
